import { useCallback } from 'react';

import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useOpenDocument } from '@/features/vfo/hooks/useOpenDocument';
import { useTasks } from '@/features/vfo/hooks/useTasks';
import { ApiTaskDocumentResponseV2 } from '@/shared/api';
import { MenuItem } from '@/shared/uikit/DropdownMenu';
import { files } from '@/shared/utils/files';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

type Props = {
  taskId: string;
  canEdit: boolean;
};

export const useDocumentActions = ({ taskId, canEdit }: Props) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'editTask' });
  const modals = useModalsStore();
  const { downloadDocument } = useDocuments();
  const { removeTaskDocument } = useTasks();
  const openDocument = useOpenDocument();

  const getActions = useCallback(
    (document: ApiTaskDocumentResponseV2) => {
      const openAction: MenuItem = {
        label: t('documentActions.open'),
        icon: 'book-open-text',
        onClick: () => openDocument(document),
      };

      const downloadAction: MenuItem = {
        label: t('documentActions.download'),
        icon: 'download',
        onClick: async () => {
          const response = await downloadDocument(document.filePath);
          if (response) {
            let filename = document.fileName;
            const titleHasExt = files.hasExtension(document.title);
            const ext = files.getExtension(document.fileName);
            if (!titleHasExt && ext) {
              filename = `${document.title}.${ext}`;
            }
            files.downloadFileByUrl(response.fileContent, filename);
          }
        },
      };

      const deleteAction: MenuItem = {
        label: t('documentActions.delete'),
        icon: 'trash',
        color: 'red',
        withSeparator: true,
        onClick: () => {
          modals.openModal({
            type: 'ConfirmModalV2',
            variant: 'delete',
            title: t('confirmDeletion.title'),
            bodyText: t('confirmDeletion.bodyText'),
            confirmText: t('confirmDeletion.confirmText'),
            onConfirm: async () => {
              await removeTaskDocument(taskId, document.id);
            },
          });
        },
      };

      return [openAction, downloadAction, ...(canEdit ? [deleteAction] : [])];
    },
    [taskId, canEdit, openDocument, downloadDocument],
  );

  return { getActions };
};
